'use client';

import { c as _c } from "react/compiler-runtime";
import { useCheckQuest } from '@morfi/backend-sdk-v2/client';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { onEvent } from '@/events';
import { pickApiCode } from '@/lib/errors/resolve-api-error';
import { trackAction } from '@/lib/telemetry';
import { fireRewardBurst } from '@/lib/ui/confetti';
type ConfettiOrigin = {
  x: number;
  y: number;
};

/**
 * Per-quest verification state machine. Engagement quests are now verified
 * on-demand against the X read API (no webhook), so the UX is: open the action
 * on X → return → we poll the `check` endpoint for ~5s (X is eventually
 * consistent, so a like/follow takes a few seconds to be queryable) →
 * verified, or "unverified" with an entry point back to the action + a manual
 * re-verify. A quest with no external action (self-attest / account-create)
 * verifies immediately.
 *
 * Two terminal phases beyond "unverified" carry the WHY (backend `apiCode`):
 *   unavailable → the X read provider could not answer (503). We KEEP polling
 *                 inside the window — the usual cause is a cold history-walk
 *                 still warming server-side (single-flighted there, so retries
 *                 are cheap) — and only surface this phase if the window closes
 *                 without an answer ("try again in a few minutes").
 *   private     → the participant's X account is protected, so their likes /
 *                 reposts / replies are invisible to verification by X design.
 *   restricted  → X is limiting the account's visibility (shadowban): their
 *                 posts are hidden from every read surface, so post-based
 *                 quests cannot be verified until X lifts it.
 */
export type QuestPhase = 'idle' | 'verifying' | 'unverified' | 'unavailable' | 'private' | 'restricted';
interface VerifiableQuest {
  id: string;
  actionUrl: string;
  status: number;
  verifyMethod: number;
}

/** How long we keep re-checking after the user returns (X eventual consistency).
 *  Sized ABOVE the backend's verify budget: a cold check may spend up to ~8s
 *  walking a viral post's history (server-side walk budget) plus a 12s
 *  twitterapi.io read — the old 5s window abandoned the poll while the server
 *  was still working, which read as "I did it but it says not verified". */
const POLL_WINDOW_MS = 16_000;
const POLL_INTERVAL_MS = 1500;
/** Hard cap on a single check so a stalled/retrying request can't hang the
 *  button. Must exceed the server's 8s walk budget + one 12s provider read. */
const ATTEMPT_TIMEOUT_MS = 15_000;
export interface QuestVerification {
  /** Transient phase for a not-yet-completed quest (server `status === 1` wins). */
  phaseOf: (id: string) => QuestPhase;
  /** Start the quest: open the X/Telegram action and arm the return-poll. No-action quests verify now.
   *  `origin` is the clicked button's viewport position, so success confetti bursts from it. */
  start: (quest: VerifiableQuest, origin?: ConfettiOrigin) => void;
  /** Re-open the action (entry point back to it) and re-arm the poll. */
  redo: (quest: VerifiableQuest, origin?: ConfettiOrigin) => void;
  /** Manually re-run verification now (the "Re-verify" button). */
  reverify: (id: string, origin?: ConfettiOrigin) => void;
}
function openAction(url: string): void {
  if (url && typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
}
async function withAttemptTimeout<T>(operation: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([operation, new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
    })]);
  } finally {
    clearTimeout(timer);
  }
}
export function useQuestVerification(quests, onChanged) {
  const $ = _c(28);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      meta: {
        skipGlobalErrorHandler: true
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const check = useCheckQuest(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [phase, setPhase] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const attempted = useRef(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const polling = useRef(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map();
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const origins = useRef(t4);
  let t5;
  if ($[5] !== check) {
    t5 = async function attempt(id) {
      ;
      try {
        await withAttemptTimeout(check.mutateAsync({
          taskId: id
        }));
        return "ok";
      } catch (t6) {
        const e = t6;
        const code = pickApiCode(e);
        if (code === "REWARDS_ALREADY_CLAIMED") {
          return "ok";
        }
        if (code === "REWARDS_VERIFY_UNAVAILABLE") {
          return "unavailable";
        }
        if (code === "REWARDS_X_ACCOUNT_PRIVATE") {
          return "private";
        }
        if (code === "REWARDS_X_ACCOUNT_RESTRICTED") {
          return "restricted";
        }
        return "unverified";
      }
    };
    $[5] = check;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const attempt = t5;
  let t6;
  if ($[7] !== attempt || $[8] !== onChanged) {
    t6 = async function poll(id_0) {
      if (polling.current.has(id_0)) {
        return;
      }
      polling.current.add(id_0);
      setPhase(p => ({
        ...p,
        [id_0]: "verifying"
      }));
      const deadline = Date.now() + POLL_WINDOW_MS;
      let outcome = "unverified";
      while (Date.now() < deadline) {
        outcome = await attempt(id_0);
        if (outcome === "ok" || outcome === "private" || outcome === "restricted") {
          break;
        }
        await new Promise(_temp);
      }
      polling.current.delete(id_0);
      attempted.current.delete(id_0);
      if (outcome === "ok") {
        onEvent("rewards_quest_verified", {
          questId: id_0
        });
        fireRewardBurst(origins.current.get(id_0));
        origins.current.delete(id_0);
        await onChanged();
        setPhase(p_0 => {
          const next = {
            ...p_0
          };
          delete next[id_0];
          return next;
        });
      } else {
        trackAction("rewards_quest_verify_failed", {
          questId: id_0,
          reason: outcome
        });
        onEvent("rewards_quest_verify_failed", {
          questId: id_0,
          reason: outcome
        });
        setPhase(p_1 => ({
          ...p_1,
          [id_0]: outcome
        }));
      }
    };
    $[7] = attempt;
    $[8] = onChanged;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const poll = t6;
  let t7;
  if ($[10] !== poll) {
    t7 = function start(quest, origin) {
      onEvent("rewards_quest_started", {
        questId: quest.id,
        verifyMethod: quest.verifyMethod
      });
      origins.current.set(quest.id, origin);
      if (quest.actionUrl) {
        openAction(quest.actionUrl);
        attempted.current.add(quest.id);
      } else {
        poll(quest.id);
      }
    };
    $[10] = poll;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const start = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = function redo(quest_0, origin_0) {
      origins.current.set(quest_0.id, origin_0);
      openAction(quest_0.actionUrl);
      attempted.current.add(quest_0.id);
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const redo = t8;
  let t9;
  if ($[13] !== poll) {
    t9 = function reverify(id_1, origin_1) {
      origins.current.set(id_1, origin_1);
      poll(id_1);
    };
    $[13] = poll;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const reverify = t9;
  let t10;
  if ($[15] !== phase || $[16] !== poll || $[17] !== quests) {
    t10 = () => {
      for (const quest_1 of quests) {
        if (quest_1.status !== 1 && attempted.current.has(quest_1.id) && phase[quest_1.id] !== "verifying") {
          poll(quest_1.id);
        }
      }
    };
    $[15] = phase;
    $[16] = poll;
    $[17] = quests;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const pollOnReturn = useEffectEvent(t10);
  let t11;
  if ($[19] !== pollOnReturn) {
    t11 = () => {
      if (typeof document === "undefined") {
        return;
      }
      const onReturn = () => {
        if (document.visibilityState !== "visible") {
          return;
        }
        pollOnReturn();
      };
      document.addEventListener("visibilitychange", onReturn);
      window.addEventListener("focus", onReturn);
      return () => {
        document.removeEventListener("visibilitychange", onReturn);
        window.removeEventListener("focus", onReturn);
      };
    };
    $[19] = pollOnReturn;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [];
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  if ($[22] !== phase) {
    t13 = id_2 => phase[id_2] ?? "idle";
    $[22] = phase;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== reverify || $[25] !== start || $[26] !== t13) {
    t14 = {
      phaseOf: t13,
      start,
      redo,
      reverify
    };
    $[24] = reverify;
    $[25] = start;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}
function _temp(r) {
  return setTimeout(r, POLL_INTERVAL_MS);
}